/**
 * The Truckstop row as the service delivers it to `board:<user_id>` (service/src/loadboard/truckstopMap.ts
 * `BoardRow`, design 5.1). It lives in ONE person's browser memory and is never stored, logged or sent
 * anywhere but back into a Coreway load the dispatcher creates from it (design 1, 6.4). Pure.
 */

export interface BoardRow {
  id: string;
  broker_company_id: string | null;
  origin_city: string | null;
  origin_state: string | null;
  origin_country: string | null;
  origin_distance_mi: number | null;
  dest_city: string | null;
  dest_state: string | null;
  dest_country: string | null;
  /** The origin's local day as Truckstop wrote it (zone-less `YYYY-MM-DD`). */
  pickup_date: string | null;
  pickup_time: string | null;
  delivery_date: string | null;
  delivery_time: string | null;
  equipment_code: string | null;
  load_type: string | null;
  length_ft: number | null;
  weight_lbs: number | null;
  miles: number | null;
  stops: number | null;
  /** The posted total in cents; null = not posted. */
  rate_cents: number | null;
  broker_name: string | null;
  broker_mc: string | null;
  broker_dot: string | null;
  broker_phone: string | null;
  broker_email: string | null;
  broker_contact: string | null;
  broker_contact_phone: string | null;
  broker_credit: string | null;
  broker_days_to_pay: number | null;
  notes: string | null;
  age: string | null;
  is_deleted: boolean;
}

const TEXT_KEYS = [
  'broker_company_id', 'origin_city', 'origin_state', 'origin_country', 'dest_city', 'dest_state', 'dest_country',
  'pickup_date', 'pickup_time', 'delivery_date', 'delivery_time', 'equipment_code', 'load_type', 'broker_name',
  'broker_mc', 'broker_dot', 'broker_phone', 'broker_email', 'broker_contact', 'broker_contact_phone',
  'broker_credit', 'notes', 'age',
] as const;
const NUMBER_KEYS = [
  'origin_distance_mi', 'length_ft', 'weight_lbs', 'miles', 'stops', 'rate_cents', 'broker_days_to_pay',
] as const;

/** The Truckstop id a load can be booked from (`load_sources.source_ref`, 0355). */
export const BOARD_ROW_ID = /^[0-9]{1,12}$/;
const YMD = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Shallow validation of one delivered row (design 6.5): the id must be a Truckstop id, else null (the row
 * cannot be keyed nor booked); every other field of the wrong type reads as null (a date that is not
 * `YYYY-MM-DD` too), `is_deleted` is true only when it is literally true. Unknown keys are dropped.
 */
export function parseBoardRow(data: unknown): BoardRow | null {
  if (typeof data !== 'object' || data === null || Array.isArray(data)) return null;
  const r = data as Record<string, unknown>;
  if (typeof r.id !== 'string' || !BOARD_ROW_ID.test(r.id)) return null;
  const out: Record<string, unknown> = { id: r.id };
  for (const k of TEXT_KEYS) out[k] = typeof r[k] === 'string' ? r[k] : null;
  for (const k of NUMBER_KEYS) {
    const v = r[k];
    out[k] = typeof v === 'number' && Number.isFinite(v) ? v : null;
  }
  for (const k of ['pickup_date', 'delivery_date'] as const) {
    if (typeof out[k] === 'string' && !YMD.test(out[k] as string)) out[k] = null;
  }
  out.is_deleted = r.is_deleted === true;
  return out as unknown as BoardRow;
}

/** Every outcome a `loadboard.run` publication may carry (service delivery.ts `RunMessageOutcome`). */
export const RUN_OUTCOMES = [
  'ok', 'auth_failed', 'partner_auth_failed', 'provider_refused', 'bad_request', 'bad_response', 'transient',
  'not_configured', 'skipped', 'expired',
] as const;
export type RunOutcome = (typeof RUN_OUTCOMES)[number];
